import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LevelKey } from 'src/app/Widgets/static-blocks/enrollment-form/enrollment-form.component';

interface IngresoStep {
  number: string;
  icon: string;
  title: string;
  description: string;
}

/** Tabs de la página; cada uno ofrece los niveles de su modalidad (CUA-133) */
type InscriptionTab = 'basic' | 'superior';

interface InscriptionTabDef {
  id: InscriptionTab;
  label: string;
  /** Modalidades que el formulario deja elegir mientras el tab está activo */
  levelKeys: LevelKey[];
  /** El "Pasos para el Ingreso" sólo aplica a los niveles obligatorios */
  showSteps: boolean;
}

/** Parámetro de URL que abre un tab directo: /inscriptions?tab=superior */
const TAB_QUERY_PARAM = 'tab';

/** Valores aceptados en `?tab=` para entrar al tab de Nivel Superior */
const SUPERIOR_PARAM_VALUES = ['superior', 'terciario', 'tertiary'];

/** Ruta de la página; la usan otras páginas para armar sus links de inscripción */
export const INSCRIPTIONS_PATH = '/inscriptions';

/** Link que entra directo al tab de Nivel Superior (botones de inscripción por nivel) */
export const SUPERIOR_INSCRIPTIONS_LINK = `${INSCRIPTIONS_PATH}?${TAB_QUERY_PARAM}=superior`;

@Component({
  selector: 'app-inscriptions',
  templateUrl: './Inscriptions.component.html',
  styleUrls: ['./Inscriptions.component.scss']
})
export class InscriptionsComponent implements OnInit {
  readonly tabs: InscriptionTabDef[] = [
    {
      id: 'basic',
      label: 'Niveles Inicial / Primario / Secundario',
      levelKeys: ['inicial', 'primario', 'secundario'],
      showSteps: true
    },
    {
      id: 'superior',
      label: 'Nivel Superior',
      levelKeys: ['terciario'],
      showSteps: false
    }
  ];

  activeTab: InscriptionTab = 'basic';

  /** "Pasos para el Ingreso" cards shown in the hero */
  readonly steps: IngresoStep[] = [
    {
      number: '1',
      icon: 'fas fa-pen',
      title: 'Formulario',
      description: 'Completá el formulario de inscripción online con los datos del aspirante y la familia.'
    },
    {
      number: '2',
      icon: 'fas fa-comment-dots',
      title: 'Entrevista',
      description: 'Coordinamos un encuentro con la familia y el equipo directivo del nivel correspondiente.'
    },
    {
      number: '3',
      icon: 'fas fa-graduation-cap',
      title: 'Matriculación',
      description: 'Presentación de documentación y formalización de la inscripción para el ciclo lectivo.'
    }
  ];

  constructor(private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    // El link puede llegar con ?tab=superior y tiene que abrir ese tab directo
    this.route.queryParams.subscribe(params => {
      this.activeTab = this.tabFromParam(params[TAB_QUERY_PARAM]);
    });
  }

  get activeTabDef(): InscriptionTabDef {
    return this.tabs.find(tab => tab.id === this.activeTab) || this.tabs[0];
  }

  /** Los pasos se ocultan en Nivel Superior, que no tiene ese circuito */
  get showSteps(): boolean {
    return this.activeTabDef.showSteps;
  }

  /** Modalidades que el formulario ofrece para el tab activo */
  get activeLevelKeys(): LevelKey[] {
    return this.activeTabDef.levelKeys;
  }

  /** Cambiar de tab reescribe `?tab=` para que la URL siga siendo compartible */
  setActiveTab(tab: InscriptionTab): void {
    if (tab === this.activeTab) {
      return;
    }
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [TAB_QUERY_PARAM]: tab === 'superior' ? 'superior' : null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /** Subrayado deslizante del tab activo (mismo patrón que LevelProfile) */
  getSlideStyles() {
    const widthPercent = 100 / this.tabs.length;
    const activeIndex = Math.max(0, this.tabs.findIndex(tab => tab.id === this.activeTab));

    return {
      width: `${widthPercent}%`,
      left: `${widthPercent * activeIndex}%`
    };
  }

  private tabFromParam(value: any): InscriptionTab {
    const raw = String(value || '').toLowerCase().trim();
    return SUPERIOR_PARAM_VALUES.indexOf(raw) !== -1 ? 'superior' : 'basic';
  }
}
